<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useLayout } from '@/layout/composables/layout'
import { ApiClientFactory } from '@/stores/apiClientFactory'
import type { GetSeatsResponse } from '@/api-client/models'

/**
 * Trial countdown, as a chip at the foot of the sidebar. Deliberately global rather than living on one
 * page: a trial ending is about the account, and someone who never opens Sales Reps would otherwise never
 * see it. It sits in the nav rather than across the top of the content because a countdown is ambient —
 * it should stay in view without taking a band out of every page.
 *
 * Renders nothing at all when there is no trial — including when the call fails, since a chip is not
 * worth an error and a wrong one is worse than none.
 */
const client = ApiClientFactory.getInstance().getClient()
const router = useRouter()
const { isSlim } = useLayout()

const usage = ref<GetSeatsResponse | null>(null)

const inTrial = computed(() => usage.value?.inTrial ?? false)
const daysRemaining = computed(() => usage.value?.trialDaysRemaining ?? null)

/** Warn harder as it runs out — a week is a nudge, two days is a deadline. */
const urgent = computed(() => daysRemaining.value !== null && daysRemaining.value <= 3)

/** The chip has room for a count, not a sentence, so the noun is carried by the title instead. */
const label = computed(() => {
  const days = daysRemaining.value
  if (days === null) return 'Free trial'
  if (days === 0) return 'Ends today'
  if (days === 1) return '1 day left'
  return `${days} days left`
})

const title = computed(() => {
  const days = daysRemaining.value
  if (days === null) return 'You’re on a free trial. Open Billing.'
  if (days === 0) return 'Your free trial ends today. Open Billing.'
  if (days === 1) return 'Your free trial ends tomorrow. Open Billing.'
  return `${days} days left of your free trial. Open Billing.`
})

onMounted(async () => {
  try {
    usage.value = (await client.api.organizations.subscriptions.seats.get()) ?? null
  } catch {
    // No chip rather than an error: this is ambient information, not something the page needs.
    usage.value = null
  }
})
</script>

<template>
  <div v-if="inTrial" class="trial-chip-slot" :class="{ 'trial-chip-slot--slim': isSlim }">
    <button type="button" class="trial-chip" :class="{ 'trial-chip--urgent': urgent }" :title="title"
      :aria-label="title" @click="router.push({ path: '/billing' })">
      <i class="pi pi-clock trial-chip__icon" aria-hidden="true" />
      <span v-if="!isSlim" class="trial-chip__label">{{ label }}</span>
    </button>
  </div>
</template>

<style scoped>
/* Sits between the menu and the account button, so it keeps the sidebar's own gutters rather than the
   content width it used to span. */
.trial-chip-slot {
    padding: 0 1rem 0.25rem;
}

.trial-chip-slot--slim {
    display: flex;
    justify-content: center;
    padding: 0 0 0.25rem;
}

.trial-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 5px 12px;
    border: 1px solid #cfe0ec;
    border-radius: 999px;
    background: #e8f1f8;
    color: #0c2530;
    font: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.3;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;
}

.trial-chip:hover {
    background: #d9e8f4;
    border-color: #b6d1e5;
}

.trial-chip--urgent {
    background: #fdf0e6;
    border-color: #f3d4b6;
    color: #7a3d06;
}

.trial-chip--urgent:hover {
    background: #fbe4d2;
    border-color: #eec49c;
}

.trial-chip__icon {
    font-size: 0.75rem;
    flex-shrink: 0;
}

.trial-chip__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Icon-only in the collapsed rail: the label has nowhere to go, and the title carries the detail. */
.trial-chip-slot--slim .trial-chip {
    padding: 6px;
}
</style>
